import * as JsonDecoder from "ts.data.json";

import { Controller } from "@hotwired/stimulus";
import { createRoot, Root } from "react-dom/client";
import React from "react";
import DispatchDashboard from "src/admin/dispatch/dashboard";
import { IDispatchClient, ReadonlyClient } from "src/admin/dispatch/dispatch_api_client";
import { Dashboard, DeliveryTracking, DeliveryTrackingStep } from "src/admin/dispatch/model";

export default class VirtualDispatchController extends Controller {
  static targets = ["form", "dashboard", "fileInput", "error", "banner", "bannerText"];

  declare fileInputTarget: HTMLInputElement;
  declare formTarget: HTMLElement;
  declare dashboardTarget: HTMLDivElement;
  declare errorTarget: HTMLElement;
  declare bannerTarget: HTMLElement;
  declare bannerTextTarget: HTMLElement;

  private dashboardRoot?: Root;
  private apiClient?: IDispatchClient;

  connect(): void {
    const stored = sessionStorage.getItem("virtual-dispatch-dump");
    if (stored) this.renderDump(stored);
  }

  async loadDump(event: Event): Promise<void> {
    event.preventDefault();

    const file = this.fileInputTarget.files?.[0];
    if (!file) return;

    const textContent = await file.text();
    this.errorTarget.textContent = "";

    if (!this.renderDump(textContent)) return;

    try {
      sessionStorage.setItem("virtual-dispatch-dump", textContent);
    } catch {
      this.bannerTextTarget.textContent += " — too large to save";
    }
  }

  private renderDump(textContent: string): boolean {
    let json: { capturedAt: string; dashboard: unknown, driverTracking: Record<string, unknown> };
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      json = JSON.parse(textContent);
    } catch (e) {
      this.errorTarget.textContent = `Invalid JSON: ${(e as Error).message}`;
      return false;
    }

    const dashboardData = this.translateLegacyFields(json.dashboard);
    const dashboardResult = Dashboard.decoder.decode(dashboardData);
    if (!dashboardResult.isOk()) {
      this.errorTarget.textContent = `Failed to decode dashboard: ${dashboardResult.error}`;
      return false;
    }

    const driverTracking = new Map<number, DeliveryTracking[]>();
    for (const [locationKey, tracking] of Object.entries(json.driverTracking)) {
      const match = locationKey.match(/\d+/)

      if (match) {
        const extractedId = parseInt(match[0]);
        const decoded = VirtualDispatchController.trackingDecoder.decode(tracking);

        if (decoded.isOk()) {
          const unpackedTrackingDump = decoded.value;
          const unpackedDeliveries = Object.values(unpackedTrackingDump.deliveries).flat();

          const deliveryTracking = Object.values(unpackedDeliveries).flat().map(row => ({
            uid: row.deliveryUid,
            liveLastUpdate: unpackedTrackingDump.receivedAt,
            liveArrivalEstimate: new Date(unpackedTrackingDump.receivedAt.getTime() + 1000 * row.secondsToDropoff),
            routeToPickup: row.routeToPickup as DeliveryTrackingStep[],
            routeToDropoff: row.routeToDropoff as DeliveryTrackingStep[],
          }));

          driverTracking.set(extractedId, deliveryTracking as DeliveryTracking[]);
        }
      }
    }

    // Tear down any previously-rendered dashboard before replacing it.
    this.apiClient?.stop();
    this.dashboardRoot?.unmount();

    const capturedAt = new Date(json.capturedAt);
    this.apiClient = new ReadonlyClient(-1, "", dashboardResult.value, driverTracking);
    this.apiClient.start();

    this.bannerTextTarget.textContent = `Virtual dashboard — frozen at ${capturedAt.toLocaleString()}`;
    this.bannerTarget.classList.remove("is-hidden");
    this.formTarget.classList.add("is-hidden");
    this.dashboardTarget.classList.remove("is-hidden");

    this.dashboardRoot = createRoot(this.dashboardTarget);
    this.dashboardRoot.render(
      React.createElement(DispatchDashboard, {
        apiClient: this.apiClient,
        currentAdminId: 0,
        fixedTime: new Date(json.capturedAt),
      })
    );

    return true;
  }

  reset(): void {
    sessionStorage.removeItem("virtual-dispatch-dump");
    window.location.reload();
  }

  disconnect(): void {
    this.dashboardRoot?.unmount();
    this.dashboardRoot = undefined;
    this.apiClient?.stop();
  }

  /* eslint-disable no-multi-spaces */ // commit-date columns aligned below
  translateLegacyFields(dashboard: unknown): unknown {
    if (dashboard === null || typeof dashboard !== "object") return dashboard;
    const dash = dashboard as Record<string, unknown>;

    dash.autogroupingStatus ??= "hidden"; // 2026-06-11 (01b0c4fe95)
    dash.areaSlug ??= "";                 // 2026-06-16 (d9aa1e4151)
    dash.tasks ??= null;                  // 2026-06-16 (d9aa1e4151)

    if (Array.isArray(dash.deliveries)) {
      for (const delivery of dash.deliveries) {
        if (delivery === null || typeof delivery !== "object") continue;
        const del = delivery as Record<string, unknown>;

        del.storeHref ??= "";     // 2026-06-10 (e22e35a69a)
        del.collectedAt ??= null; // 2026-06-22 (a86db83f93)

        if (del.deliveryStatus === "grouped") del.deliveryStatus = "requested"; // Removed 2026-05-25 (023d8fae06)
      }
    }

    return dash;
  }
  /* eslint-enable no-multi-spaces */

  static readonly trackingDecoder = JsonDecoder.object({
    driverId: JsonDecoder.number(),
    receivedAt: JsonDecoder.string().map(sd => new Date(sd)),
    deliveries: JsonDecoder.record(
      JsonDecoder.array(
        JsonDecoder.object({
          deliveryUid: JsonDecoder.string(),
          secondsToDropoff: JsonDecoder.number(),
          routeToDropoff: JsonDecoder.succeed(),
          routeToPickup: JsonDecoder.succeed()
        }, "TrackingDelivery"),
        "TrackingDelivery[]"),
      "TrackingDeliveryRecord")
  }, "DumpedTracking");
}
